<template>
  <section class="personal-center-page">
    <div v-if="!front.isLoggedIn" class="personal-center-login">
      <h2>登录后查看个人服务</h2>
      <p>登录后可管理账户、会员、分会、会议与缴费等个人业务。</p>
      <button type="button" class="primary-action compact" @click="front.openAuth('login')">登录 / 注册</button>
    </div>

    <div v-else class="personal-center-shell">
      <div ref="sidebarRoot" class="personal-center-sidebar-root">
        <div
          v-show="isFloating"
          class="personal-center-sidebar-spacer"
          :style="{ height: spacerHeight + 'px' }"
          aria-hidden="true"
        />
        <aside
          ref="sidebarPanel"
          class="personal-center-sidebar"
          :class="{ 'is-floating': isFloating }"
          :style="panelStyle"
          aria-label="个人中心导航"
        >
          <div class="personal-center-sidebar-head">
            <strong>个人中心</strong>
          </div>
          <nav class="personal-center-nav">
            <button
              v-for="item in sections"
              :key="item.value"
              type="button"
              :class="{ active: item.value === activeSection }"
              @click="selectSection(item.value)"
            >
              {{ item.label }}
            </button>
          </nav>
        </aside>
      </div>

      <main class="personal-center-content">
        <component :is="activePanel" />
      </main>
    </div>
  </section>
</template>

<script>
import PersonalOverviewPanel from '@/components/personal/panels/PersonalOverviewPanel.vue'
import PersonalAccountPanel from '@/components/personal/panels/PersonalAccountPanel.vue'
import PersonalMembershipPanel from '@/components/personal/panels/PersonalMembershipPanel.vue'
import PersonalMemberCertPanel from '@/components/personal/panels/PersonalMemberCertPanel.vue'
import PersonalBranchesPanel from '@/components/personal/panels/PersonalBranchesPanel.vue'
import PersonalConferencesPanel from '@/components/personal/panels/PersonalConferencesPanel.vue'
import PersonalParticipantPanel from '@/components/personal/panels/PersonalParticipantPanel.vue'
import PersonalPaymentsPanel from '@/components/personal/panels/PersonalPaymentsPanel.vue'
import PersonalNotificationsPanel from '@/components/personal/panels/PersonalNotificationsPanel.vue'
import PersonalSecurityPanel from '@/components/personal/panels/PersonalSecurityPanel.vue'
import LoginRecordsPanel from '@/components/personal/panels/LoginRecordsPanel.vue'

const panels = {
  overview: PersonalOverviewPanel,
  account: PersonalAccountPanel,
  membership: PersonalMembershipPanel,
  'member-cert': PersonalMemberCertPanel,
  branches: PersonalBranchesPanel,
  conferences: PersonalConferencesPanel,
  participant: PersonalParticipantPanel,
  payments: PersonalPaymentsPanel,
  notifications: PersonalNotificationsPanel,
  security: PersonalSecurityPanel,
  'login-records': LoginRecordsPanel
}

const FLOAT_TOP = 148
const FLOAT_BOTTOM_GAP = 20
const FLOAT_MQ = '(max-width: 900px)'

export default {
  name: 'PersonalCenterPage',
  inject: ['front'],
  data() {
    return {
      isFloating: false,
      spacerHeight: 0,
      panelStyle: {},
      rafId: 0,
      sections: [
        { value: 'overview', label: '概览' },
        { value: 'account', label: '账户资料' },
        { value: 'membership', label: '会员服务' },
        { value: 'member-cert', label: '电子会员证' },
        { value: 'branches', label: '分会绑定' },
        { value: 'conferences', label: '我的会议' },
        { value: 'participant', label: '参会信息' },
        { value: 'payments', label: '缴费记录' },
        { value: 'notifications', label: '消息通知' },
        { value: 'login-records', label: '登录记录' },
        { value: 'security', label: '账户安全' }
      ]
    }
  },
  computed: {
    activeSection() {
      const section = this.$route.query.section
      return Object.prototype.hasOwnProperty.call(panels, section) ? section : 'overview'
    },
    activePanel() {
      return panels[this.activeSection]
    }
  },
  watch: {
    '$route.query.section': {
      immediate: true,
      handler(section) {
        if (this.front.needsForcePasswordChange && section !== 'security') {
          this.$router.replace({
            path: this.$route.path,
            query: { ...this.$route.query, section: 'security' }
          })
          return
        }
        if (!Object.prototype.hasOwnProperty.call(panels, section)) {
          this.$router.replace({
            path: this.$route.path,
            query: { ...this.$route.query, section: 'overview' }
          })
        }
        this.$nextTick(() => this.scheduleFloating())
      }
    },
    'front.needsForcePasswordChange': {
      immediate: true,
      handler(needed) {
        if (needed && this.$route.query.section !== 'security') {
          this.$router.replace({
            path: this.$route.path,
            query: { ...this.$route.query, section: 'security' }
          })
        }
      }
    }
  },
  mounted() {
    this.scheduleFloating()
    window.addEventListener('scroll', this.scheduleFloating, { passive: true })
    window.addEventListener('resize', this.scheduleFloating)
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.scheduleFloating)
    window.removeEventListener('resize', this.scheduleFloating)
    if (this.rafId) {
      cancelAnimationFrame(this.rafId)
      this.rafId = 0
    }
  },
  methods: {
    selectSection(section) {
      if (this.front.needsForcePasswordChange && section !== 'security') {
        this.front.selfServiceStatus = '为保障账号安全，请先修改初始密码后再使用其他功能'
        section = 'security'
      }
      if (section === this.activeSection) return
      this.$router.push({
        path: this.$route.path,
        query: { ...this.$route.query, section }
      })
    },
    scheduleFloating() {
      if (this.rafId) return
      this.rafId = requestAnimationFrame(() => {
        this.rafId = 0
        this.updateFloating()
      })
    },
    resetFloating() {
      this.isFloating = false
      this.spacerHeight = 0
      this.panelStyle = {}
    },
    updateFloating() {
      if (typeof window === 'undefined' || window.matchMedia(FLOAT_MQ).matches) {
        this.resetFloating()
        return
      }
      const root = this.$refs.sidebarRoot
      const panel = this.$refs.sidebarPanel
      if (!root || !panel) {
        this.resetFloating()
        return
      }
      const layout = root.closest('.personal-center-shell')
      if (!layout) {
        this.resetFloating()
        return
      }

      const slotRect = root.getBoundingClientRect()
      const layoutRect = layout.getBoundingClientRect()
      const naturalHeight = panel.offsetHeight || 0
      if (!naturalHeight || layoutRect.bottom <= FLOAT_TOP) {
        this.resetFloating()
        return
      }

      if (slotRect.top > FLOAT_TOP) {
        this.resetFloating()
        return
      }

      let top = FLOAT_TOP
      const maxTop = layoutRect.bottom - naturalHeight - FLOAT_BOTTOM_GAP
      if (maxTop < FLOAT_TOP) {
        top = Math.max(12, maxTop)
      }

      this.isFloating = true
      this.spacerHeight = naturalHeight
      this.panelStyle = {
        position: 'fixed',
        top: `${Math.round(top)}px`,
        left: `${Math.round(slotRect.left)}px`,
        width: `${Math.round(slotRect.width)}px`,
        zIndex: 30
      }
    }
  }
}
</script>

<style scoped>
.personal-center-sidebar-root {
  position: relative;
  min-width: 0;
}

.personal-center-sidebar-spacer {
  width: 100%;
  pointer-events: none;
}

.personal-center-sidebar.is-floating {
  box-sizing: border-box;
}
</style>
